<script setup lang="ts">
import { makeDeepRef } from "@/components/composables/componentTools";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";

import messages from "@/plugins/i18n";
import type { PlannedSalesVisit } from "@/app/services/generated/salesvisitService";
import userStore from "@/store/modules/userStore";
import PlannedSalesVisitHeader from "@/components/pages/orders/salesvisits/components/PlannedSalesVisitHeader.vue";
import { newPlannedSalesVisit } from "../useSalesVisitData";

export interface CreateOrEditSalesVisitDialogProps {
  title?: string;
  salesVisit?: PlannedSalesVisit;
  showEntity?: boolean;
  showUser?: boolean;
  showDate?: boolean;
}

export interface CreateOrEditSalesVisitEvents extends DialogEvents<PlannedSalesVisit> {}

const props = withDefaults(defineProps<CreateOrEditSalesVisitDialogProps>(), {
  salesVisit: () => {
    const newSalesVisit = newPlannedSalesVisit({
      userId: userStore.state.user?.id,
      isPlannedTimeFlexible: true,
    });

    return newSalesVisit;
  },
  showEntity: true,
  showUser: true,
  showDate: true,
});

const emit = defineEmits<CreateOrEditSalesVisitEvents>();

const { accept, cancel } =
  useReactiveDialogScriptSetup<PlannedSalesVisit>(emit);

const internalSalesVisit = makeDeepRef<PlannedSalesVisit>(
  props.salesVisit.clone()
);
</script>

<template>
  <bm-dialog
    :label="title || messages.sales_visit_details"
    @accept="() => accept(internalSalesVisit)"
    @cancel="cancel"
  >
    <planned-sales-visit-header
      :value.sync="internalSalesVisit"
      header-size="single-width"
      is-editing
      :show-entity="showEntity"
      :show-date="showDate"
      :show-user="showUser"
    />
  </bm-dialog>
</template>
